import {Alert, Typography, Box, useMediaQuery, Snackbar} from '@mui/material';
import {useNavigate} from 'react-router-dom';
import {useState, useEffect, useContext, useMemo} from 'react';

import {SkinConditionsBase} from './base';
import ConfirmationDialog from '../../components/dialogbox/confirmationDialog';
import {useDermaApi} from '../../api/derma';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {calculateAge, calculateDuration} from '../../utils/miscellaneous';
import {delay} from '../../utils/miscellaneous';

// dermadetect components
import {DermadetectInitial} from '../../components/dermaDetails/dermadetectInitial';
import {BodyLocationsComponent} from '../../components/dermaDetails/bodyLocations/allBodyLocations';
import {BooleanSelectButtonGroup} from '../../components/APIForms/booleanQuestion';
import {MultipleOptionQuestion} from '../../components/APIForms/multipleOptionQuestion';
import {CheckboxQuestion} from '../../components/APIForms/checkboxQuestion';
import {ScaleQuestionComponent} from '../../components/APIForms/scaleQuestionComponent';

// Contexts
import APIObjectsContext from '../../context/APIObjectsContext';
import APIServiceFormContext from '../../context/APIServiceFormContext';

import {
  faDrySkin,
  faScaleSkin,
  faExfoliationSkin,
  faSingleLocationSkinCondition,
  faClusteredLocationsSkinCondition,
  faSkinConditionDistributed,
  faAmorphous,
  faRound,
  faLightningBoltBordered,
} from '../../assets/icons/';
import {faHandDots, faCircle, faHand as faFilledHand} from '@fortawesome/free-solid-svg-icons';
import {
  faHand as faThinHand,
  faInfinity,
  faMinimize,
  faMaximize,
  faArrowTrendDown,
} from '@fortawesome/pro-light-svg-icons';
import {useQuestionnaireApi} from '../../api/demographics';
import {useAssessmentApi} from '../../api/assessment';
import {iconColor, iconSize} from '../../components/APIForms/variables';
import {AnimationLoadingScreenWithProgressBar} from '../../components/loading/loadingScreen';

//options that are passed to some question components
const distribution_options = [
  {
    value: 'single',
    title: 'A single location',
    icon: faSingleLocationSkinCondition,
  },
  {
    value: 'multiple',
    title: 'Clusters in more than two locations',
    icon: faClusteredLocationsSkinCondition,
  },
  {
    value: 'widespread',
    title: 'Distributed across the body',
    icon: faSkinConditionDistributed,
  },
];
const texture_options = [
  {
    value: 'dry',
    title: 'Dry',
    subtitle: 'The area is drier than usual',
    icon: faDrySkin,
  },
  {
    value: 'scales',
    title: 'Scales',
    subtitle: 'Coarse and marked by overlapping flakes',
    icon: faScaleSkin,
  },
  {
    value: 'cracks',
    title: 'Cracks',
    subtitle: 'Brittle, fragmented, and marred by crevices',
    icon: faLightningBoltBordered,
  },
  {
    value: 'exfoliation',
    title: 'Exfoliation',
    subtitle: 'Irritated, and stripped of its natural moisture.',
    icon: faExfoliationSkin,
  },
];
const size_options = [
  {
    value: 'pea_or_smaller',
    cornerLabel: 'Small',
    description: 'Smaller than pea size',
  },
  {
    value: 'wristwatch_or_smaller',
    description: 'Larger than pea size but smaller than a wrist watch',
  },
  {
    value: 'palm_or_smaller',
    description: 'Larger than wristwatch but smaller than a palm',
  },
  {
    value: 'bigger_than_palm',
    cornerLabel: 'Large',
    description: 'Larger than a palm',
  },
];
const condition_options = [
  {
    title: 'Above skin level',
    value: 'above',
    icon: faHandDots,
  },
  {
    title: 'On skin level',
    value: 'skin_level',
    icon: faFilledHand,
  },
  {
    title: 'Beneath skin level',
    value: 'below',
    icon: faThinHand,
  },
];
// this one is going to be processed before passing it to a question prop
const colourList = [
  {value: 'white', title: 'White'},
  {value: 'red', title: 'Red'},
  {value: 'brown', title: 'Brown'},
  {value: 'blue', title: 'Blue'},
  {value: 'gray', title: 'Grey'},
  {value: 'purple', title: 'Purple'},
  {value: 'black', title: 'Black'},
  {value: 'yellow', title: 'Yellow'},
  {value: 'green', title: 'Green'},
];

/** list of all questions keys and their respective components, important part for the dermadetect form system
 *
 *  key:string // dermadetects key of a required question
 *  name:string // question name
 *  component:React.Component // question component from `pages/dermaDetails/`
 *
 */
const questions = [
  {
    key: 'initial',
    name: 'DermadetectInitial',
    component: <DermadetectInitial />,
  },
  {
    key: /^location[1-3]\.(primary|secondary)$/,
    name: 'body location primary',
    component: <BodyLocationsComponent />,
  },
  {
    key: 'bleeding',
    name: 'body location primary',
    component: (
      <BooleanSelectButtonGroup
        title="Is there any visible wet or dry bleeding?"
        choices={[
          {
            value: true,
            text: 'Yes',
          },
          {
            value: false,
            text: 'No',
          },
        ]}
      />
    ),
  },
  {
    key: 'elevation',
    name: 'elevation skin condition',
    component: (
      <MultipleOptionQuestion
        title="At what level is the skin condition?"
        choices={condition_options.map(opt => ({
          title: opt.title,
          value: opt.value,
          icon: <FontAwesomeIcon icon={opt.icon} style={{color: iconColor, fontSize: iconSize}} />,
        }))}
      />
    ),
  },
  {
    key: 'color',
    name: 'color skin condition',
    component: (
      <CheckboxQuestion
        title={'Select all of the colours that you can see in the skin condition.'}
        childXS={5.5}
        childSM={3.5}
        itemHeight={'60px'}
        choices={colourList.map(item => {
          return {
            value: item.value,
            title: item.title,
            icon: <FontAwesomeIcon icon={faCircle} style={{color: item.value, fontSize: iconSize - 5}} />,
          };
        })}
      />
    ),
  },
  {
    key: 'distribution',
    name: 'skin body distribution',
    component: (
      <MultipleOptionQuestion
        title="How is the skin condition distributed throughout your body?"
        choices={distribution_options.map(option => ({
          value: option.value,
          title: option.title,
          icon: <FontAwesomeIcon icon={option.icon} style={{color: iconColor, fontSize: iconSize}} />,
        }))}
      />
    ),
  },
  {
    key: 'texture',
    name: 'skin condition characteristics',
    component: (
      <CheckboxQuestion
        title={'What are the characteristics of the skin condition?'}
        childXS={12}
        childSM={5.7}
        choices={texture_options.map(item => {
          return {
            value: item.value,
            title: item.title,
            subtitle: item.subtitle,
            icon: <FontAwesomeIcon icon={item.icon} style={{color: iconColor, fontSize: iconSize}} />,
          };
        })}
      />
    ),
  },
  {
    key: 'size',
    name: 'size skin condition',
    component: (
      <ScaleQuestionComponent
        title={'What is the size of the skin condition?'}
        minimum={0}
        maximum={size_options.length - 1}
        marks={size_options}
      />
    ),
  },
  {
    key: 'shape',
    name: 'shape skin condition',
    component: (
      <MultipleOptionQuestion
        title="What is the shape of the skin condition?"
        choices={[
          {
            value: 'round',
            title: 'Round',
            icon: <FontAwesomeIcon icon={faRound} style={{color: iconColor, fontSize: iconSize}} />,
          },
          {
            value: 'non-round',
            title: 'Not Round',
            icon: <FontAwesomeIcon icon={faAmorphous} style={{color: iconColor, fontSize: iconSize}} />,
          },
        ]}
      />
    ),
  },
  {
    key: 'swelling',
    name: 'swelling skin condition',
    component: (
      <BooleanSelectButtonGroup
        title="Is the area swollen?"
        width="300px"
        choices={[
          {
            value: true,
            text: 'Yes',
          },
          {
            value: false,
            text: 'No',
          },
        ]}
      />
    ),
  },
  {
    key: 'vesicle',
    name: 'vesicle skin condition',
    component: (
      <BooleanSelectButtonGroup
        title="Does it look like a blister?"
        width="300px"
        choices={[
          {
            value: true,
            text: 'Yes',
          },
          {
            value: false,
            text: 'No',
          },
        ]}
      />
    ),
  },
  {
    key: 'pus',
    name: 'pus skin condition',
    component: (
      <BooleanSelectButtonGroup
        title="Does the affected area show any signs of pus or abnormal fluid?"
        choices={[
          {
            value: true,
            text: 'Yes',
          },
          {
            value: false,
            text: 'No',
          },
        ]}
      />
    ),
  },
  {
    key: 'hair_loss.exists',
    name: 'hair loss',
    component: (
      <BooleanSelectButtonGroup
        title="Is there hair loss in the problem area?"
        choices={[
          {
            value: true,
            text: 'Yes',
          },
          {
            value: false,
            text: 'No',
          },
        ]}
      />
    ),
  },
  {
    key: 'hair_loss.type',
    name: 'hair loss type',
    component: (
      <MultipleOptionQuestion
        title={'Please specify the type of hair loss.'}
        choices={[
          {
            value: 'focal',
            title: 'Focal',
            subtitle: 'Only where the condition is.',
            icon: (
              <FontAwesomeIcon
                icon={faMinimize}
                style={{
                  color: iconColor,
                  fontSize: iconSize,
                  textAlign: 'center',
                }}
              />
            ),
          },
          {
            value: 'spread',
            title: 'Widespread',
            subtitle: 'Spread across the body.',
            icon: <FontAwesomeIcon icon={faMaximize} style={{color: iconColor, fontSize: iconSize}} />,
          },
        ]}
      />
    ),
  },
  {
    key: 'pain.type',
    name: 'pain type',
    component: (
      <MultipleOptionQuestion
        title={'Please specify the type of pain.'}
        choices={[
          {
            value: 'constant',
            title: 'Constant Pain',
            subtitle: 'The affected area is constantly in pain.',
            icon: <FontAwesomeIcon icon={faInfinity} style={{color: iconColor, fontSize: iconSize}} />,
          },
          {
            value: 'on_pressure',
            title: 'Pain Upon Contact',
            subtitle: 'There is pain when pressure is applied to the affected area.',
            icon: <FontAwesomeIcon icon={faArrowTrendDown} style={{color: iconColor, fontSize: iconSize}} />,
          },
        ]}
      />
    ),
  },
  {
    key: 'pain.scale_0_to_5',
    name: 'pain scale 0 to 5',
    component: (
      <ScaleQuestionComponent
        minimum={0}
        maximum={5}
        title={'Do you feel pain in the affected area?'}
        subtitle={'Select a value from 0 to 5 in the slider above where 0 is no pain and 5 is extremely painful.'}
      />
    ),
  },
  {
    key: 'itch.scale_0_to_5',
    name: 'itch scale 0 to 5',
    component: (
      <ScaleQuestionComponent
        minimum={0}
        maximum={5}
        title={'Does the affected area itch?'}
        subtitle={'Select a value from 0 to 5 in the slider above where 0 is no itch at all and 5 is extremely itchy.'}
      />
    ),
  },
  {
    key: 'fever.exists',
    name: 'fever.exists',
    component: (
      <BooleanSelectButtonGroup
        title="Are you running a fever above 38 degrees?"
        choices={[
          {
            value: true,
            text: 'Yes',
          },
          {
            value: false,
            text: 'No',
          },
        ]}
      />
    ),
  },
  {
    key: 'fever.is_24h',
    name: 'fever.is_24h',
    component: (
      <BooleanSelectButtonGroup
        title="Have you had the fever for more than 24 hours?"
        choices={[
          {
            value: true,
            text: 'Yes',
          },
          {
            value: false,
            text: 'No',
          },
        ]}
      />
    ),
  },
];

// DERMADETECT FORM PAGE HELPER FUNCTIONS

/**
 * This function assumes that question dependencies have already been handled (and that the question key is the last question in the form). Depends on a fixed order of the questions
 *
 * @param {string} questionKey - input question key
 *
 * @returns {string} next question key
 */
const getNextQuestionBasedOnQuestionKey = questionKey => {
  if (questionKey === '' || questionKey === 'initial') {
    return 'location1.primary';
  }
  if (questionKey.match(/^(location[1-3]\.(primary|secondary)|(add_location_[2-3]))$/)) return 'texture';

  if (questionKey === 'texture') return 'bleeding';
  if (questionKey === 'bleeding') return 'distribution';
  if (questionKey === 'distribution') return 'size';
  if (questionKey === 'size') return 'shape';
  if (questionKey === 'shape') return 'elevation';
  if (questionKey.match(/^(elevation|swelling|vesicle)$/)) return 'color';
  if (questionKey === 'color') return 'pus';

  if (questionKey === 'pus') return 'pain.scale_0_to_5';
  if (questionKey.match(/^(pain\.scale_0_to_5|pain\.type)$/)) return 'itch.scale_0_to_5';

  if (questionKey === 'itch.scale_0_to_5') return 'fever.exists';

  if (questionKey.match(/^(fever\.exists|fever\.is_24h)$/)) return 'hair_loss.exists';

  if (questionKey.match(/^(hair_loss\.type|hair_loss\.exists)$/)) return 'done';
};

/**
 * DermadetectFormPage component tracks the order of the questions and saves their data. However, some questions' answers can make the user go to additional questions that would otherwise be skipped, this object is used to manage those questions "dependencies" depending on their value.
 *
 * Each key here is the dermadetect key that has a dependency, then each key has an object as a value which has two keys: the value, which is the value from that key that triggers the dependency(it can be any value or a function accepting a value for more customization on what to accept), and an array with the dependencies, this is an array of dermadetect keys that will be shown if the value is as expected.
 */
let questionDependencies = {
  elevation: {
    value: 'above',
    dependencies: ['swelling', 'vesicle'],
  },
  'hair_loss.exists': {
    value: true,
    dependencies: ['hair_loss.type'],
  },
  'pain.scale_0_to_5': {
    value: val => val >= 2,
    dependencies: ['pain.type'],
  },
  'fever.exists': {
    value: true,
    dependencies: ['fever.is_24h'],
  },
};

/**
 * React functional component representing the Dermadetect Form Page.
 * @component
 * @returns {JSX.Element} The rendered DermadetectFormPage component.
 */
export const DermadetectFormPage = () => {
  const navigate = useNavigate();
  const isSmallScreen = useMediaQuery('(max-width:600px)');
  const [isLoading, setIsLoading] = useState(false);
  const [diagnosisFinishedAnalysing, setDiagnosisFinishedAnalysing] = useState(false);
  const initialText = isSmallScreen ? 'Start' : 'Start my screening';
  const dermaApi = useDermaApi();
  const assessmentApi = useAssessmentApi();
  const questionnaireApi = useQuestionnaireApi();

  const [confirmDialog, setConfirmDialog] = useState({
    title: '',
    subTitle: '',
    isOpen: false,
  });
  //way to display and handle validation
  const [errorMessage, setErrorMessage] = useState('');
  const [validationFunc, setValidationFunc] = useState(() => {
    function firstValidationFunc() {
      return '';
    }
    return firstValidationFunc;
  });

  const [sendDataArgs, setSendDataArgs] = useState({data: ''});

  const {
    anamnesisObject,
    setAnamnesisObject,
    anamnesisObjectAdditionalDataValues: additionalDataValues,
    setAnamnesisObjectAdditionalDataValues: setAdditionalDataValues,
    anamnesisObjectQuestionKeysHistory: questionKeysHistory,
    setAnamnesisObjectQuestionKeysHistory: setQuestionKeysHistory,
    userAssessmentID,
  } = useContext(APIObjectsContext);
  // an array that will be used as a stack to track the questions the user has seen, and the order of those
  const [questionHistoryIndex, setQuestionHistoryIndex] = useState(-1);

  /** total amount of questions that the user has to fill in. This updates based on which question the user answered considering the dependent questions and all */
  const totalQuestions = useMemo(() => {
    let totalQuestions = questions.length - 1;
    for (let question of Object.keys(questionDependencies)) {
      if (!questionKeysHistory.includes(question)) continue;

      if (
        anamnesisObject[question] === undefined ||
        anamnesisObject[question] === questionDependencies[question].value ||
        (typeof questionDependencies[question].value === 'function' &&
          questionDependencies[question].value(anamnesisObject[question]))
      )
        continue;

      totalQuestions -= questionDependencies[question].dependencies.length;
    }
    return totalQuestions;
  }, [questionKeysHistory]);
  const [errorMessageOpen, setErrorMessageOpen] = useState(false);

  //depends on anamnesis object and additional data values
  const [savedValue, setSavedValue] = useState(undefined);
  const [additionalSavedData, setAdditionalSavedData] = useState(undefined);

  const [currentQuestion, setCurrentQuestion] = useState({
    name: '',
    question: '',
    type: '',
    key: '',
  });

  const [CurrentComponent, setCurrentComponent] = useState(
    questions[0].component, //initialComponent
  );

  useEffect(() => {
    if (validationFunc.instantError === undefined) return;

    setErrorMessage(validationFunc.instantError);
    setErrorMessageOpen(true);
  }, [validationFunc]);

  /* sets the first pieces of data of the anamnesisObject based on the data we already have**/
  useEffect(() => {
    let firstAnamnesisObject = {};
    questionnaireApi
      .getQuestionnaire()
      .then(res => {
        if (res.status !== 200) return;

        const data = res.data;
        firstAnamnesisObject = {
          gender: data.gender_id === 1 ? 'male' : data.gender_id === 2 || data.gender_id === 3 ? 'female' : null,
          age: calculateAge(data.date_of_birth),
          pregnancy: data.gender_id === 1 || data.gender_id === 4 ? undefined : data.pregnant,
        };

        assessmentApi
          .getAssessment(userAssessmentID)
          .then(res => {
            firstAnamnesisObject.free_text_description = res.data.symptoms_description;
            firstAnamnesisObject.duration_days = calculateDuration(res.data.symptoms_started_at);
            let newAnamnesisObject = {
              ...anamnesisObject,
              ...firstAnamnesisObject,
            };
            setAnamnesisObject(newAnamnesisObject);
          })
          .catch(err => console.error(err));
      })
      .catch(() => navigate('/start')); // in case something goes wrong go to login page. This can avoid the errors of proceeding in the form without being authenticated
  }, []);

  /* saves the data from each question to the anamnesisObject and also saves the additional data. Both of those to have ready when contacting the API and for allowing the user to freely navigate back and forth between all the questions **/
  useEffect(() => {
    // add conditional for specific keys
    if (currentQuestion.key.match(/^location[1-3]\.(primary|secondary)$/)) {
      const numberOfAttributesPerLocation = 3;
      const matchingKeys = Object.keys(anamnesisObject).filter(key => key.match(/^location[1-3]/));
      const totalLocations = matchingKeys.length / numberOfAttributesPerLocation;
      let newLocationData = [];
      for (let i = 0; i < totalLocations; i++) {
        newLocationData.push({
          primary: anamnesisObject[`location${i + 1}.primary`],
          secondary: anamnesisObject[`location${i + 1}.secondary`],
          image_urls: anamnesisObject[`location${i + 1}.image_urls`],
        });
      }
      setSavedValue(newLocationData);
      setAdditionalSavedData(additionalDataValues[currentQuestion.key]);
      return;
    }
    setSavedValue(anamnesisObject[currentQuestion.key]);
    setAdditionalSavedData(additionalDataValues[currentQuestion.key]);
  }, [currentQuestion, anamnesisObject, additionalDataValues]);

  useEffect(() => {
    if (currentQuestion.key === undefined || currentQuestion.key === '') return;
    handleVisibilityComponent(currentQuestion.key);
  }, [additionalSavedData, savedValue]);

  /**
   * Essential part of dermadetects form system. This function will make sure to extract the current question's component data and put it where it should go.
   */
  function sendData() {
    function sendData1(data) {
      let newAnamnesisObject = {
        ...anamnesisObject,
        [currentQuestion.key]: data,
      };
      setAnamnesisObject(newAnamnesisObject);
      return newAnamnesisObject;
    }

    function sendData2(data, keyHandling) {
      let staticKeyPart = keyHandling.staticParts;
      let changingPart =
        keyHandling.changingPart === undefined ? '' : currentQuestion.key.match(keyHandling.changingPart)[0];
      let newData = {};

      if (staticKeyPart !== undefined || staticKeyPart.length !== 0) {
        data.forEach((answer, idx) => {
          newData[changingPart + staticKeyPart[idx]] = answer;
        });
      } else {
        data.forEach(answer => {
          newData[changingPart] = answer;
        });
      }

      let newAnamnesisObject = {...anamnesisObject, ...newData};

      if (currentQuestion.key.match(/^location[1-3]\.(primary|secondary)$/)) {
        let newDataKeys = Object.keys(newData);
        let locationRelatedKeys = Object.keys(newAnamnesisObject).filter(key =>
          key.match(/^(location[1-3])|(add_location_[2-3])/),
        );
        for (let key of locationRelatedKeys) {
          if (newDataKeys.includes(key)) continue;
          delete newAnamnesisObject[key];
        }
      }
      setAnamnesisObject(newAnamnesisObject);
      return newAnamnesisObject;
    }
    const areArgumentsValid =
      arguments.length === 1 ||
      (arguments.length === 2 &&
        Array.isArray(arguments[0]) &&
        Array.isArray(arguments[1].staticParts) &&
        (arguments[1].changingPart instanceof RegExp || arguments[1].changingPart === undefined));

    if (!areArgumentsValid) {
      return;
    }

    let newAnamnesisObject = {};
    let question = questions.filter(comp => {
      if (comp.key instanceof RegExp) return currentQuestion.key.match(comp.key);

      return comp.key === currentQuestion.key;
    })[0]; //first index since there will only be one element

    if (arguments.length === 1) {
      newAnamnesisObject = sendData1(arguments[0]);
    }
    if (
      arguments.length === 2 &&
      Array.isArray(arguments[0]) &&
      Array.isArray(arguments[1].staticParts) &&
      (arguments[1].changingPart instanceof RegExp || arguments[1].changingPart === undefined)
    ) {
      newAnamnesisObject = sendData2(arguments[0], arguments[1]);
    }
    return newAnamnesisObject;
  }

  const handleBack = () => {
    let lastQuestionKey = questionKeysHistory[questionHistoryIndex - 1];
    if (questionHistoryIndex === -1) {
      setConfirmDialog({
        title: 'Are you sure you want to cancel?',
        subTitle: 'If you cancel, you will lose your progress.',
        isOpen: true,
        onConfirm: () => {
          setConfirmDialog({
            ...confirmDialog,
            isOpen: false,
          });
          navigate('/demographics');
        },
      });
      return;
    }

    //updating the object
    let newObject = anamnesisObject;
    if (currentQuestion.key !== '') {
      newObject =
        sendDataArgs.keyHandling === undefined
          ? (newObject = sendData(sendDataArgs.data)) //first version of the function
          : (newObject = sendData(sendDataArgs.data, sendDataArgs.keyHandling));
    }
    if (sendDataArgs.additional !== undefined) {
      let newAdditionalDataValues = {
        ...additionalDataValues,
        [currentQuestion.key]: sendDataArgs.additional,
      };
      setAdditionalDataValues(newAdditionalDataValues);
    }

    if (questionHistoryIndex === 0) {
      setQuestionHistoryIndex(-1);
      setErrorMessage('');
      setCurrentQuestion({key: ''});
      handleVisibilityComponent('');
      return;
    }

    setQuestionHistoryIndex(questionHistoryIndex - 1);
    setCurrentQuestion({
      key: lastQuestionKey,
    });
    handleVisibilityComponent(lastQuestionKey);
  };

  /**
   * Based on a question key(assuming it is the last one the user is). Updates both the history of questions and the anamnesisObject to manage any dependencies. For example, lets say that the user has filled all the questions and then returns to a question that was triggering a dependency and changes the value to another one that doesn't trigger a dependency. In that case this function will update the history to delete the dependant questions since they are not needed and also delete those keys from the anamnesisObject. Then returns those 2 values in an object so they can be updated
   *
   * @param {string} questionKeyToCheck
   * @param {Object} currentAnamnesisObject - Object following the anamnesisObject guidelines from dermadetect
   *
   * @returns {Object} Object with the updated anamnesisObject and updated questionHistory too
   */
  const questionDependencyManagement = (questionKeyToCheck, currentAnamnesisObject) => {
    if (!Object.keys(questionDependencies).includes(questionKeyToCheck)) return;
    const currentValue = currentAnamnesisObject[questionKeyToCheck];
    const questionDependencyData = questionDependencies[questionKeyToCheck];
    const dependentQuestions = questionDependencyData.dependencies;

    if (
      (typeof questionDependencyData.value === 'function' && questionDependencyData.value(currentValue)) ||
      currentValue === questionDependencyData.value
    ) {
      let dependentsAlreadyIncludedInQuestionHistory = false;
      for (const question of dependentQuestions) {
        if (!questionKeysHistory.includes(question)) continue;

        dependentsAlreadyIncludedInQuestionHistory = true;
        break;
      }
      if (dependentsAlreadyIncludedInQuestionHistory) return;
      let newQuestionHistory = [
        ...questionKeysHistory.slice(0, questionHistoryIndex + 1),
        ...dependentQuestions.filter(q => !Array.isArray(q)),
        ...questionKeysHistory.slice(questionHistoryIndex + 1),
      ];
      setQuestionKeysHistory(newQuestionHistory);

      return {currentAnamnesisObject, newQuestionHistory};
    }
    //if it is not equal to the value that generates the dependent questions, then the dependent questions must be deleted from the question history if they exist

    // assuming dependent questions are after the current question(and that current question value is the dependency)
    dependentQuestions.forEach(q => {
      if (!Array.isArray(q)) {
        delete currentAnamnesisObject[q];
        return;
      }
      q.forEach(dependent => delete currentAnamnesisObject[dependent]);
    });
    setAnamnesisObject(currentAnamnesisObject);

    let dependentQuestionIndexes = [];
    for (let i = questionHistoryIndex; i < questionKeysHistory.length; i++) {
      if (!dependentQuestions.includes(questionKeysHistory[i])) continue;
      dependentQuestionIndexes.push(i);
    }
    const newQuestionHistory = questionKeysHistory.filter((_, idx) => !dependentQuestionIndexes.includes(idx));
    setQuestionKeysHistory(newQuestionHistory);
    return {currentAnamnesisObject, newQuestionHistory};
  };

  const handleNext = () => {
    // validation
    let possibleErrorMessage = CurrentComponent.type === DermadetectInitial ? '' : validationFunc();
    setErrorMessage(possibleErrorMessage);
    if (possibleErrorMessage !== '') {
      setErrorMessageOpen(true);
      return;
    }

    let newObject = anamnesisObject;
    if (currentQuestion.key !== '') {
      newObject =
        sendDataArgs.keyHandling === undefined
          ? (newObject = sendData(sendDataArgs.data)) //first version of the function
          : (newObject = sendData(sendDataArgs.data, sendDataArgs.keyHandling));
    }

    if (sendDataArgs.additional !== undefined) {
      let newAdditionalDataValues = {
        ...additionalDataValues,
        [currentQuestion.key]: sendDataArgs.additional,
      };
      setAdditionalDataValues(newAdditionalDataValues);
    }

    let newQuestionKeysHistory = questionKeysHistory;
    try {
      const {currentAnamnesisObject, newQuestionHistory} = questionDependencyManagement(currentQuestion.key, newObject);
      newObject = currentAnamnesisObject;
      newQuestionKeysHistory = newQuestionHistory;
    } catch {}

    //verify if we are in the last question from question history
    if (questionHistoryIndex < newQuestionKeysHistory.length - 1) {
      const newQuestionKey = newQuestionKeysHistory[questionHistoryIndex + 1];
      setCurrentQuestion({key: newQuestionKey});
      handleVisibilityComponent(newQuestionKey);
      setQuestionHistoryIndex(questionHistoryIndex + 1);
      return;
    }

    //Simulating Calling derma-api "http://0.0.0.0:8000/next_questions" endpoint
    const newKey = getNextQuestionBasedOnQuestionKey(currentQuestion.key);
    if (newKey === 'done') {
      setIsLoading(true);
      dermaApi.postDiagnose(newObject).then(() => {
        setDiagnosisFinishedAnalysing(true);
        delay(2).then(() => {
          setIsLoading(false);
          navigate('/continue');
        });
      }).catch(() => {
        setIsLoading(false);
        navigate('/skin-condition-error');
      });
      return;
    }

    setCurrentQuestion({key: newKey});
    const presentQuestionKeys = Object.keys(newObject);
    let currentKeyHistory = questionKeysHistory.filter(key => presentQuestionKeys.includes(key));
    currentKeyHistory.push(newKey);
    setQuestionKeysHistory(currentKeyHistory);
    setQuestionHistoryIndex(currentKeyHistory.length - 1);
    handleVisibilityComponent(newKey);
  };

  // Update visibility of the main component(the question that is being shown)
  const handleVisibilityComponent = questionKey => {
    if (questionKey === '') {
      const initialComponent = questions.filter(q => q.key === 'initial')[0].component;
      setCurrentComponent(initialComponent);
      return;
    }
    if (questionKey === 'done') {
      navigate('/pre-face-scan');
    }

    let question = questions.filter(comp => {
      if (comp.key instanceof RegExp) return questionKey.match(comp.key);

      return comp.key === questionKey;
    })[0]; //first index since there will only be one element
    if (question === undefined) question = '';
    setCurrentComponent(question.component);
  };

  return (
    <SkinConditionsBase
      dontAnimate
      pageNumber={CurrentComponent === undefined || CurrentComponent.type !== DermadetectInitial ? 1 : 0}
      nextButtonName={
        CurrentComponent === undefined || CurrentComponent.type !== DermadetectInitial ? 'Next' : initialText
      }
      formProgress={((questionHistoryIndex + 1) / totalQuestions) * 95}
      backButtonName={CurrentComponent.type === DermadetectInitial ? 'Cancel' : 'Back'}
      handleBack={handleBack}
      backButtonIcon={CurrentComponent.type === DermadetectInitial ? '' : undefined}
      handleNext={handleNext}
    >
      <APIServiceFormContext.Provider
        value={{
          setValidationFunc,
          setSendDataArgs,
          savedValue,
          additionalSavedData,
        }}
      >
        {CurrentComponent.type !== DermadetectInitial && CurrentComponent.type !== BodyLocationsComponent && (
          <Typography variant="h4" sx={{my: 2}}>
            Additional Details
          </Typography>
        )}
        {isLoading && (
          <AnimationLoadingScreenWithProgressBar
            title="Analyzing data..."
            secondsToLoad={30}
            isAlmostComplete={diagnosisFinishedAnalysing}
          />
        )}
        <Box
          sx={{
            width: 'fit-content',
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            textAlign: 'center',
            alignItems: 'center',
            position: 'relative',
          }}
        >
          {CurrentComponent}

          {errorMessage !== '' && (
            <Snackbar
              autoHideDuration={5000}
              open={errorMessageOpen}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'center',
              }}
              onClose={() => {
                setErrorMessageOpen(false);
              }}
            >
              <Alert
                severity="error"
                sx={{
                  '& .MuiSvgIcon-root': {
                    fontSize: 20,
                  },
                  display: 'flex',
                  alignItems: 'center',
                  textAlign: 'center',
                  mb: 8,
                  fontSize: {
                    xs: 16,
                    sm: 18,
                  },
                  px: {
                    xs: 2,
                    sm: 5,
                  },
                  borderRadius: 3,
                }}
              >
                {errorMessage.charAt(errorMessage.length - 1) === '.' ? errorMessage : errorMessage + '.'}
              </Alert>
            </Snackbar>
          )}
        </Box>
      </APIServiceFormContext.Provider>
      {/* Confirmation dialog box when user clicks Cancel button on footer */}
      <ConfirmationDialog confirmDialog={confirmDialog} setConfirmDialog={setConfirmDialog} />
    </SkinConditionsBase>
  );
};
